import React, { useEffect, useState } from "react";
import Reveal from "./Reveal";

const scrollToForm = () => {
  const el = document.getElementById("rezervace");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

// Deadline: konec května 2026 (poslední den, 23:59:59 lokálního času)
const DEADLINE = new Date(2026, 5, 30, 23, 59, 59); // měsíc je 0-indexovaný → 5 = červen

function useCountdown(target) {
  const [diff, setDiff] = useState(() => Math.max(target.getTime() - Date.now(), 0));
  useEffect(() => {
    const t = setInterval(() => setDiff(Math.max(target.getTime() - Date.now(), 0)), 1000);
    return () => clearInterval(t);
  }, [target]);
  const totalSec = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
    expired: diff <= 0,
  };
}

export const Scarcity = () => {
  const { days, hours, minutes, seconds, expired } = useCountdown(DEADLINE);

  return (
    <section
      id="nedostatek"
      data-testid="scarcity-section"
      className="relative py-16 sm:py-20 md:py-28 bg-[#070707] overflow-hidden"
    >
      {/* Subtle yellow glow */}
      <div className="absolute inset-0 -z-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-hormozi-yellow/[0.06] blur-3xl" />
      </div>

      <div className="relative max-w-5xl mx-auto px-5 sm:px-6 md:px-10">
        <Reveal className="text-center">
          <div className="inline-flex items-center gap-2 bg-hormozi-yellow text-black px-3 py-1.5 mb-5 sm:mb-6">
            <span className="relative flex h-2 w-2">
              <span className="absolute inset-0 rounded-full bg-black/70 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-black" />
            </span>
            <span className="text-[11px] sm:text-xs tracking-wide uppercase font-black">
              ▲ Omezená akce
            </span>
          </div>

          <h2
            data-testid="scarcity-headline"
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-white leading-[0.95] tracking-tight"
          >
            Akce končí
            <br />
            <span className="text-hormozi-yellow">30. června.</span>
          </h2>

          <p className="mt-5 sm:mt-6 text-sm sm:text-base md:text-lg text-zinc-300 font-medium max-w-xl mx-auto leading-snug">
            Po tomto datu se ceny voucherů vrací na <span className="text-white font-bold">plnou hodnotu</span>.
            Tyto podmínky už znovu nevypíšeme.
          </p>
        </Reveal>

        <Reveal delay={150} className="mt-10 sm:mt-12">
          {expired ? (
            <div
              data-testid="scarcity-expired"
              className="border-2 border-zinc-700 bg-black/60 p-8 sm:p-10 text-center max-w-2xl mx-auto"
            >
              <div className="text-[11px] tracking-wide uppercase font-bold text-zinc-500 mb-2">
                Akce ukončena
              </div>
              <div className="font-display text-2xl sm:text-3xl text-white mb-3">
                Aktuální ceny vidíte na webu
              </div>
              <button
                onClick={scrollToForm}
                data-testid="scarcity-cta-button"
                className="mt-4 bg-hormozi-yellow hover:bg-[#FFE34D] text-black px-6 sm:px-8 py-3.5 sm:py-4 text-xs sm:text-sm tracking-tight uppercase font-black transition-colors"
              >
                Zobrazit voucher →
              </button>
            </div>
          ) : (
            <div
              data-testid="scarcity-countdown-card"
              className="border-2 border-hormozi-yellow bg-black/70 backdrop-blur-sm max-w-3xl mx-auto"
            >
              <div className="bg-hormozi-yellow text-black px-5 sm:px-6 py-2.5 text-center">
                <span className="text-[11px] sm:text-xs tracking-wide uppercase font-black">
                  Do konce akce zbývá
                </span>
              </div>

              <div className="p-5 sm:p-7 md:p-9">
                <div
                  data-testid="scarcity-countdown"
                  className="grid grid-cols-4 gap-2 sm:gap-3 md:gap-4 text-center"
                >
                  {[
                    { label: "Dnů", value: days },
                    { label: "Hodin", value: hours },
                    { label: "Minut", value: minutes },
                    { label: "Sekund", value: seconds },
                  ].map((u, i) => (
                    <div
                      key={u.label}
                      className={`bg-black/60 border ${i === 0 ? "border-hormozi-yellow" : "border-white/15"} py-4 sm:py-5 md:py-6`}
                    >
                      <div className={`font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-none tabular-nums ${i === 0 ? "text-hormozi-yellow" : "text-white"}`}>
                        {String(u.value).padStart(2, "0")}
                      </div>
                      <div className="text-[10px] sm:text-[11px] tracking-wide uppercase text-zinc-500 font-bold mt-2 sm:mt-3">
                        {u.label}
                      </div>
                    </div>
                  ))}
                </div>

                <button
                  onClick={scrollToForm}
                  data-testid="scarcity-cta-button"
                  className="mt-6 sm:mt-7 w-full bg-hormozi-yellow hover:bg-[#FFE34D] text-black px-6 sm:px-8 py-4 sm:py-5 text-sm sm:text-base tracking-tight uppercase font-black transition-colors"
                >
                  Zajistit voucher dokud platí akce →
                </button>
              </div>
            </div>
          )}
        </Reveal>

        <Reveal delay={250} className="mt-7 sm:mt-9 flex flex-wrap items-center justify-center gap-x-5 sm:gap-x-7 gap-y-2 text-[11px] sm:text-xs text-zinc-400 font-medium">
          <span>✓ Bez závazku</span>
          <span className="text-zinc-700">•</span>
          <span>✓ Platnost 3 roky od aktivace</span>
          <span className="text-zinc-700">•</span>
          <span>✓ Přenositelný — i jako dárek</span>
        </Reveal>
      </div>
    </section>
  );
};

export default Scarcity;
